import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  Button,
  TextField,
  MenuItem,
  Popover,
  Chip,
  Badge,
  Tabs,
  Tab,
  InputAdornment,
  Divider,
  CircularProgress,
} from '@material-ui/core';
import { SaveAlt as SaveAltIcon } from '@material-ui/icons';
import FilterListIcon from '@material-ui/icons/FilterList';
import SearchIcon from '@material-ui/icons/Search';
import CitySelector from './CitySelector';
import BannerSelector from './BannerSelector';
import {
  REPORT_TYPE_OPTIONS,
  REVIEW_FILTER_OPTIONS,
  REVIEW_CHIP_STYLES,
  STALE_PRICE_VIEWS,
  buildStalePriceReportUrl,
  countSecondaryFilters,
  getActiveFilterChips,
  DEFAULT_FILTERS,
} from './stalePriceReportConfig';

const useStyles = makeStyles((theme) => ({
  appBar: {
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
  },
  toolbar: {
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
  },
  titleSection: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginRight: theme.spacing(2),
  },
  title: {
    fontWeight: 600,
    whiteSpace: 'nowrap',
  },
  tabs: {
    minHeight: 36,
    marginLeft: theme.spacing(1),
  },
  tab: {
    minHeight: 36,
    minWidth: 100,
    textTransform: 'none',
    fontWeight: 500,
  },
  filterGroup: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    flex: 1,
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginLeft: 'auto',
  },
  searchField: {
    minWidth: 200,
    maxWidth: 280,
  },
  caption: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    paddingBottom: theme.spacing(0.5),
    color: theme.palette.text.secondary,
    fontSize: '0.75rem',
    borderLeft: `3px solid ${theme.palette.primary.main}`,
    marginLeft: theme.spacing(2),
    marginBottom: theme.spacing(0.5),
  },
  activeFiltersRow: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(0.5),
    padding: theme.spacing(0.5, 2),
    backgroundColor: '#fafafa',
    borderTop: '1px solid #e0e0e0',
  },
  summaryBar: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    padding: theme.spacing(1, 2),
    backgroundColor: '#f5f5f5',
    borderTop: '1px solid #e0e0e0',
  },
  summaryChip: {
    cursor: 'pointer',
    '&:hover': {
      opacity: 0.85,
    },
  },
  summaryChipActive: {
    boxShadow: '0 0 0 2px rgba(0,0,0,0.2)',
  },
  filterPopover: {
    padding: theme.spacing(2),
    minWidth: 300,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
  },
  filterPopoverTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(0.5),
  },
  contentArea: {
    position: 'relative',
  },
  loadingOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(255,255,255,0.7)',
    zIndex: 2,
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(6, 2),
    textAlign: 'center',
    gap: theme.spacing(2),
  },
  tableContainer: {
    maxHeight: 'calc(100vh - 220px)',
    width: '100%',
  },
  tableRowHover: {
    '&:hover': {
      backgroundColor: '#f9f9f9',
    },
  },
  needsReviewRow: {
    backgroundColor: '#fff8f8',
  },
}));

export default function StalePriceReportLayout({
  view,
  title,
  userTypeId,
  projectId,
  token,
  filters,
  onFilterChange,
  onClearAllFilters,
  reporterOptions,
  countLabel,
  totalCount,
  needsReviewCount,
  reviewedCount,
  loading,
  onExport,
  children,
  emptyMessage,
  showEmptyState,
}) {
  const classes = useStyles();
  const [filterAnchor, setFilterAnchor] = useState(null);

  const secondaryFilterCount = countSecondaryFilters(filters);
  const activeChips = getActiveFilterChips(filters, reporterOptions);

  const handleFilterFieldChange = (field, value) => {
    onFilterChange({ ...filters, [field]: value });
  };

  const handleClearChip = (clearKey) => {
    if (clearKey === 'dates') {
      onFilterChange({ ...filters, fromDate: '', toDate: '' });
      return;
    }
    if (clearKey === 'banners') {
      onFilterChange({ ...filters, banners: [] });
      return;
    }
    onFilterChange({ ...filters, [clearKey]: DEFAULT_FILTERS[clearKey] ?? '' });
  };

  const handleReviewChipClick = (reviewValue) => {
    const next = filters.review === reviewValue ? '' : reviewValue;
    onFilterChange({ ...filters, review: next });
  };

  const dateFilterLabel =
    filters.fromDate || filters.toDate
      ? `${filters.fromDate || '...'} – ${filters.toDate || '...'}`
      : 'All dates';

  const reportsTabUrl = buildStalePriceReportUrl(
    STALE_PRICE_VIEWS.reports.path,
    userTypeId,
    projectId,
    filters
  );
  const byPmgTabUrl = buildStalePriceReportUrl(
    STALE_PRICE_VIEWS.byPmg.path,
    userTypeId,
    projectId,
    filters
  );
  const byBannerTabUrl = buildStalePriceReportUrl(
    STALE_PRICE_VIEWS.byBanner.path,
    userTypeId,
    projectId,
    filters
  );
  const byCityTabUrl = buildStalePriceReportUrl(
    STALE_PRICE_VIEWS.byCity.path,
    userTypeId,
    projectId,
    filters
  );

  return (
    <>
      <AppBar position="sticky" color="inherit" className={classes.appBar}>
        <Toolbar className={classes.toolbar}>
          <Box className={classes.titleSection}>
            <Typography variant="h6" className={classes.title}>
              {title}
            </Typography>
            <Tabs
              value={view}
              indicatorColor="primary"
              textColor="primary"
              className={classes.tabs}
            >
              <Tab
                className={classes.tab}
                label={STALE_PRICE_VIEWS.reports.label}
                value="reports"
                component={Link}
                to={reportsTabUrl}
              />
              <Tab
                className={classes.tab}
                label={STALE_PRICE_VIEWS.byPmg.label}
                value="byPmg"
                component={Link}
                to={byPmgTabUrl}
              />
              <Tab
                className={classes.tab}
                label={STALE_PRICE_VIEWS.byBanner.label}
                value="byBanner"
                component={Link}
                to={byBannerTabUrl}
              />
              <Tab
                className={classes.tab}
                label={STALE_PRICE_VIEWS.byCity.label}
                value="byCity"
                component={Link}
                to={byCityTabUrl}
              />
            </Tabs>
          </Box>

          <Box className={classes.filterGroup}>
            <CitySelector
              selectedCity={filters.city}
              onCitySelect={(city) => handleFilterFieldChange('city', city)}
              project_id={projectId}
              token={token}
            />
            <BannerSelector
              project_id={projectId}
              token={token}
              selectedBanners={filters.banners}
              onBannerSelect={(banner) => {
                if (banner === '') {
                  handleFilterFieldChange('banners', []);
                  return;
                }
                const next = filters.banners.includes(banner)
                  ? filters.banners.filter((b) => b !== banner)
                  : [...filters.banners, banner];
                handleFilterFieldChange('banners', next);
              }}
            />
            <TextField
              variant="outlined"
              size="small"
              placeholder="Search..."
              value={filters.search}
              onChange={(e) => handleFilterFieldChange('search', e.target.value)}
              className={classes.searchField}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" color="action" />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          <Box className={classes.actions}>
            <Badge badgeContent={secondaryFilterCount} color="primary">
              <Button
                variant="outlined"
                size="small"
                startIcon={<FilterListIcon />}
                onClick={(e) => setFilterAnchor(e.currentTarget)}
              >
                Filters
              </Button>
            </Badge>
            <Button
              variant="outlined"
              color="primary"
              size="small"
              startIcon={<SaveAltIcon />}
              onClick={onExport}
            >
              Export CSV
            </Button>
          </Box>
        </Toolbar>

        <Typography variant="caption" className={classes.caption}>
          Click a row&apos;s Last Collected cell to review store status
        </Typography>

        {activeChips.length > 0 && (
          <Box className={classes.activeFiltersRow}>
            <Typography variant="caption" color="textSecondary" style={{ marginRight: 4 }}>
              Active:
            </Typography>
            {activeChips.map((chip) => (
              <Chip
                key={chip.key}
                size="small"
                label={chip.label}
                onDelete={() => handleClearChip(chip.clearKey)}
              />
            ))}
            <Button size="small" onClick={onClearAllFilters} style={{ marginLeft: 4 }}>
              Clear all
            </Button>
          </Box>
        )}

        <Box className={classes.summaryBar}>
          <Typography variant="body2">
            {countLabel}: <strong>{totalCount}</strong>
          </Typography>
          <Chip
            size="small"
            label={`Needs review: ${needsReviewCount}`}
            className={`${classes.summaryChip} ${
              filters.review === 'needs_review' ? classes.summaryChipActive : ''
            }`}
            style={REVIEW_CHIP_STYLES.needsReview}
            onClick={() => handleReviewChipClick('needs_review')}
          />
          <Chip
            size="small"
            label={`Reviewed: ${reviewedCount}`}
            className={`${classes.summaryChip} ${
              filters.review === 'reviewed' ? classes.summaryChipActive : ''
            }`}
            style={REVIEW_CHIP_STYLES.reviewed}
            onClick={() => handleReviewChipClick('reviewed')}
          />
        </Box>
      </AppBar>

      <Popover
        open={Boolean(filterAnchor)}
        anchorEl={filterAnchor}
        onClose={() => setFilterAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Box className={classes.filterPopover}>
          <Typography variant="subtitle2" className={classes.filterPopoverTitle}>
            Filters
          </Typography>

          <TextField
            select
            fullWidth
            variant="outlined"
            size="small"
            label="Report type"
            value={filters.reportType}
            onChange={(e) => handleFilterFieldChange('reportType', e.target.value)}
            InputLabelProps={{ shrink: true }}
          >
            {Object.entries(REPORT_TYPE_OPTIONS).map(([key, label]) => (
              <MenuItem key={key || 'all'} value={key}>
                {label}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            fullWidth
            variant="outlined"
            size="small"
            type="date"
            label="From date"
            value={filters.fromDate}
            onChange={(e) => handleFilterFieldChange('fromDate', e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            fullWidth
            variant="outlined"
            size="small"
            type="date"
            label="To date"
            value={filters.toDate}
            onChange={(e) => handleFilterFieldChange('toDate', e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          {(filters.fromDate || filters.toDate) && (
            <Typography variant="caption" color="textSecondary">
              {dateFilterLabel}
            </Typography>
          )}

          <TextField
            select
            fullWidth
            variant="outlined"
            size="small"
            label="Reporter"
            value={filters.reporter}
            onChange={(e) => handleFilterFieldChange('reporter', e.target.value)}
            InputLabelProps={{ shrink: true }}
          >
            <MenuItem value="">All reporters</MenuItem>
            {reporterOptions.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            fullWidth
            variant="outlined"
            size="small"
            label="Review status"
            value={filters.review}
            onChange={(e) => handleFilterFieldChange('review', e.target.value)}
            InputLabelProps={{ shrink: true }}
          >
            {Object.entries(REVIEW_FILTER_OPTIONS).map(([key, label]) => (
              <MenuItem key={key || 'all'} value={key}>
                {label}
              </MenuItem>
            ))}
          </TextField>

          <Divider />

          <Button
            size="small"
            color="primary"
            onClick={() => {
              onClearAllFilters();
              setFilterAnchor(null);
            }}
          >
            Clear all filters
          </Button>
        </Box>
      </Popover>

      <Box className={classes.contentArea}>
        {loading && (
          <Box className={classes.loadingOverlay}>
            <CircularProgress size={32} />
          </Box>
        )}

        {showEmptyState ? (
          <Box className={classes.emptyState}>
            <Typography color="textSecondary">{emptyMessage}</Typography>
            <Typography variant="body2" color="textSecondary">
              Try clearing the date range or review filter.
            </Typography>
            <Button variant="outlined" size="small" onClick={onClearAllFilters}>
              Clear all filters
            </Button>
          </Box>
        ) : (
          children
        )}
      </Box>
    </>
  );
}

export { useStyles as useStalePriceReportStyles };
